/**
 * `/app/account/confirm-email` — the far end of the email-change link (`MEM-007` AC-2).
 *
 * The token is in the query string because it arrived in an email, and it is spent by a `POST`
 * rather than by loading this page. That matters: a mail client or a link scanner that prefetches
 * URLs would otherwise consume a single-use credential before the member ever saw it, and the
 * change would appear to have happened by itself.
 *
 * Behind `ProtectedRoute`, so the link is one of two things the member needs. Whoever intercepts
 * the mail still cannot move the address without also holding the session.
 */
import { Button, Card, CardContent, useErrorToast } from '@h2hspark/ui';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConfirmationCard } from '@/member/confirmation-card';

import { useConfirmEmailChange } from './api';
import { MEMBER_PROFILE_ROUTES } from './constants';

export function ConfirmEmailPage(): React.ReactElement {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const confirm = useConfirmEmailChange();
  const showError = useErrorToast();

  React.useEffect(() => {
    if (confirm.error !== null) showError(confirm.error, 'That link did not work.');
  }, [confirm.error, showError]);

  // TW 44261258: the end state is the member app's confirmation card, the same as Verify your
  // email's, so the two screens that finish an email step look alike (Brad, 2026-10-01, "Go with
  // Option A").
  if (confirm.isSuccess) {
    return (
      <div className="pb-8">
        <ConfirmationCard
          heading="Your email address is changed"
          lead={
            <>
              You now sign in with{' '}
              <span className="break-all font-brand font-bold text-foreground">
                {confirm.data.emailAddress}
              </span>
              .
            </>
          }
          action={{ to: MEMBER_PROFILE_ROUTES.account, label: 'Back to My Account' }}
          announce
        />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-8">
      <header>
        <h1 className="text-2xl font-brand font-bold">Confirm your new email address</h1>
      </header>

      <Card>
        <CardContent className="space-y-4 pt-5">
          <p className="text-sm text-muted-foreground">
            Press the button to finish moving your account to the new address. Until you do, nothing
            has changed.
          </p>
          <Button
            type="button"
            disabled={token === '' || confirm.isPending}
            onClick={() => confirm.mutate({ token })}
          >
            {confirm.isPending ? 'Confirming…' : 'Confirm the change'}
          </Button>
          {token === '' ? (
            <p role="alert" className="text-sm text-destructive">
              That link is missing its code. Ask for a new one from My Account.
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
